import { type Store } from "redux";
import { searchHistoryChanged } from "../redux/actions";
import { type BreadcrumbData, BreadcrumbType } from "../../utils/models";
import { splitSortingCode } from "../../utils/misc";
import { selectBeSortCode } from "../redux/searchSelectors";
import { type RootState } from "../../redux/store";
import { type SortDirection } from "~/Search/models";
import { type GSAPageState } from "~/GearboxSelectionAssistant/useGearboxSALogic";
import { type TechFilterDefinition } from "~/GearboxSelectionAssistant/models";
import { type HousingSearchForm } from "~/HousingSelectionAssistant/types/housingSAFormTypes";
import type { SearchPageType } from "~/Search/useSearchPageType";

export function generateUrlFromStore(store: RootState) {
  const url = new URL(window.location.href);
  const params = url.searchParams;

  const state = store.srp;
  params.set("text", state.text);

  if (state.currentPage) {
    params.set("page", state.currentPage + "");
  }
  const selectedCode = selectBeSortCode(store);
  if (selectedCode) {
    params.set("sort", selectedCode);
  }

  if (state.assistantValues.length > 0) {
    params.set("assistantValues", state.assistantValues.join(","));
  }

  if (state.isSelectionAssistant) {
    params.set("isSelectionAssistant", "ROTARY");
  }

  params.delete("filter");
  if (state.breadcrumbs.length > 0) {
    for (const bc of state.breadcrumbs) {
      switch (bc.type) {
        case BreadcrumbType.FACET: {
          params.append("filter", `FV:${bc.facetCode}:${bc.facetValueCode}:${bc.name}`);
          break;
        }
        case BreadcrumbType.TECH_VALUE: {
          params.append("filter", `TV:${bc.code}:${bc.valueType}:${bc.value}:${bc.uom}`);
          break;
        }
        default: {
          throw new Error("Unsupported Breadcrumb type");
        }
      }
    }
  }

  params.delete("productGroup");
  state.selectedProductGroups.forEach((name) => params.append("productGroup", name));

  return url.toString();
}

export interface FromUrlSearchParams {
  text: string | null;
  currentPage: string | null;
  sortCode: string;
  sortDirection: SortDirection | null;
  breadcrumbs: BreadcrumbData[];
  assistantValues: string[];
  productGroups: string[];
}

export function generateDataFromUrl(): FromUrlSearchParams {
  return parseSearchParams(new URLSearchParams(window.location.search));
}

export function generateDataFromUrlPassed(url: string): FromUrlSearchParams {
  return parseSearchParams(new URLSearchParams(url.split("?")[1]));
}

function parseSearchParams(params: URLSearchParams): FromUrlSearchParams {
  let assistantValues: string[] = [];
  if (params.has("assistantValues")) {
    assistantValues = params.get("assistantValues")!.split(",");
  }
  const breadcrumbs: BreadcrumbData[] = [];
  if (params.has("filter")) {
    for (const bcString of params.getAll("filter")) {
      const parts = bcString.split(":");
      switch (parts[0]) {
        case "FV": {
          breadcrumbs.push({
            type: BreadcrumbType.FACET,
            facetCode: parts[1],
            facetValueCode: parts[2],
            name: [parts[3], parts[4]].filter(Boolean).join(":"),
          });
          break;
        }
        case "TV": {
          breadcrumbs.push({
            type: BreadcrumbType.TECH_VALUE,
            code: parts[1],
            valueType: parts[2],
            value: +parts[3],
            uom: 4 < parts.length ? parts[4] : "",
          });
          break;
        }
        default: {
          throw new Error("Unsupported Breadcrumb type");
        }
      }
    }
  }

  let sortCode = params.get("sort") || "";
  let sortDirection: SortDirection | null = null;
  if (sortCode) {
    const sortData = splitSortingCode(sortCode);
    sortCode = sortData.code;
    sortDirection = sortData.sortingDirection;
  }
  return {
    text: params.get("text"),
    currentPage: params.get("page"),
    sortCode,
    sortDirection,
    breadcrumbs,
    assistantValues: assistantValues,
    productGroups: params.getAll("productGroup"),
  };
}

export function bindHistoryListener(store: Store<RootState>, pageType: SearchPageType) {
  // store.dispatch()
  window.addEventListener("popstate", (e: PopStateEvent) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    store.dispatch<any>(searchHistoryChanged(pageType));
  });
}

const appendGearboxDimensionValues = (
  paramsObject: URLSearchParams,
  dimensionValues: Record<string, [number | null | undefined, number | null | undefined]>,
  dimensionDefinitions: Record<string, TechFilterDefinition>,
) => {
  Object.keys(dimensionValues).forEach((filter) => {
    const min = dimensionValues[filter][0];
    const max = dimensionValues[filter][1];
    if (typeof min === "number" && dimensionDefinitions[filter].min !== min) {
      paramsObject.append(`dvaluemin${filter}`, min.toString());
    }
    if (typeof max === "number" && dimensionDefinitions[filter].max !== max) {
      paramsObject.append(`dvaluemax${filter}`, max.toString());
    }
  });
};

const appendGearboxCharacteristicValues = (
  paramsObject: URLSearchParams,
  characteristicValues: Record<string, string[]>,
) => {
  Object.keys(characteristicValues).forEach((filter) => {
    if (characteristicValues[filter].length > 0 && typeof characteristicValues[filter] !== "string") {
      characteristicValues[filter].forEach((filterValue) => {
        paramsObject.append(filter, filterValue);
      });
    }
  });
};

export const generateUrlFromGearboxFilterValues = (state: GSAPageState) => {
  const { dimensionValues, dimensionDefinitions, characteristicValues } = state;
  const params = new URLSearchParams();
  appendGearboxDimensionValues(params, dimensionValues, dimensionDefinitions);
  appendGearboxCharacteristicValues(params, characteristicValues);
  return `${window.location.pathname}?${params.toString()}`;
};

export const generateUrlFromHousingFilterValues = (data: HousingSearchForm) => {
  const params = new URLSearchParams();

  Object.keys(data).forEach((entry) => {
    if (Array.isArray(data[entry])) {
      data[entry].length &&
        data[entry].forEach((subEntry: string) => {
          params.append(entry, subEntry);
        });
    } else if (typeof data[entry] === "object") {
      if (data[entry]) {
        data[entry].min && params.append(`${entry}-valuemin`, data[entry].min);
        data[entry].max && params.append(`${entry}-valuemax`, data[entry].max);
      }
    } else {
      data[entry] && params.append(entry, data[entry]);
    }
  });

  return `${window.location.pathname}?${params.toString()}`;
};
